/**
 * เขตปลอดภัย (data/safeZones.ts): the World's `inSafeZone` already keeps PK and kidnapping out of them; this system
 * tells the client when the player steps in or out (`safeZone`, for the toast and the badge) and sends back the
 * monsters that chase the player into one (they give up and go home, as past their leash).
 *
 * Only in zones with their own safe zones (the new Yanyu, the 256 m plains): the older zones' whole-village outline
 * keeps its old rules (monsters there were always fought at the gates). The player's place is read once a step, with
 * no random draws.
 *
 * Circles (the safe-zone rule, docs/research/C0_SCENE_PLAINS.md §6; the 256 m plains) add two rules:
 * - **Edge hysteresis:** leaving is told only `EDGE` metres past the rim, so walking along the curb does not flicker
 *   the toast. Only the telling: PK and kidnapping follow the circle itself (World `inSafeZone`).
 * - **No harm from players inside:** a player's area skills already miss players inside (World `isHostile`); a
 *   player inside also sheds the debuffs other players put on them (damage over time, slows), except the opponent's in a bout (`pvpBout`), so nothing a player did
 *   outside goes on hurting them in the circle. Monsters' debuffs stay.
 */
import { activeSafeZones, hasSafeZones, safeZoneAt } from '../data/safeZones';
import { STATUSES } from '../status';
import { coreOf } from '../region/core';
import type { World } from '../world';
import type { SystemHost, WorldSystem } from './types';

/** Events (part of `WorldEvent`). `name`: the safe zone's (or the building's) name, or null. */
export type SafeZoneEvent = { type: 'safeZone'; on: boolean; name: string | null };

/** How far round the player monsters are looked at (metres). */
const REACH = 40;
/** How far past a circle's rim leaving is told (metres). */
export const EDGE = 0.5;

/** The name of the safe place the player stands in, or null when not safe ('' for a safe place without a name). */
export function safePlaceOf(w: World, at: { x: number; y: number } = w.player): string | null {
  if (!w.inSafeZone(at)) return null;
  const o = coreOf(w.map);
  const z = safeZoneAt(w.map.zone, at.x - o.x, at.y - o.y);
  if (z) return z.name;
  return w.interiors.inside ? 'ในอาคาร' : '';
}

export class SafeZonesSystem implements WorldSystem {
  readonly name = 'safeZones';
  /** Where the player was last step (undefined before the first). */
  private was: string | null | undefined;
  /** The zone has its own safe zones (set at setup; indoors the map is the building's). */
  private on = false;
  /** Its safe zones include circles (the circle rules above). */
  private circles = false;

  constructor(private readonly h: SystemHost) {}

  setup(): void {
    const zone = this.h.world.map.zone;
    this.on = hasSafeZones(zone);
    this.circles = activeSafeZones().some((s) => s.zone === zone && !!s.circle);
  }

  /** Still within `EDGE` of the circle the player was told they are in (the toast's hysteresis). */
  private nearRim(name: string, x: number, y: number): boolean {
    const w = this.h.world;
    const o = coreOf(w.map);
    return activeSafeZones().some(
      (s) => s.zone === w.map.zone && s.name === name && !!s.circle && Math.hypot(x - o.x - s.circle.at[0], y - o.y - s.circle.at[1]) < s.circle.r + EDGE,
    );
  }

  /** Players inside shed the debuffs other players put on them. */
  private shedPlayerHarm(): void {
    const w = this.h.world;
    const people = (id: number | undefined) => {
      const e = id === undefined ? undefined : w.entities.get(id);
      return !!e && (e.kind === 'player' || e.kind === 'rival');
    };
    for (const e of w.entities.values()) {
      if (e.kind !== 'player' || e.dead || !e.statuses.length || !w.inSafeZone(e)) continue;
      // A bout's opponent (w.pvpBout) is not "harm from outside": their debuffs stay for the bout.
      const b = w.pvpBout;
      const foe = b && (b.a === e.id || b.b === e.id) ? (b.a === e.id ? b.b : b.a) : undefined;
      const kept = e.statuses.filter(
        (st) => STATUSES[st.id]?.kind !== 'debuff' || st.srcId === e.id || st.srcId === foe || !people(st.srcId),
      );
      if (kept.length !== e.statuses.length) e.statuses = kept;
    }
  }

  step(): void {
    if (!this.on) return;
    const w = this.h.world;
    const p = w.player;
    // A bout in the duelling ground is fought wherever it is: no toasts in the middle of it.
    let here = w.inArena() ? null : safePlaceOf(w);
    if (this.circles && here === null && this.was && !w.inArena() && this.nearRim(this.was, p.x, p.y)) here = this.was;
    if (this.circles && !w.inArena()) this.shedPlayerHarm();
    if (this.was !== undefined && (here === null) !== (this.was === null))
      this.h.emit({ type: 'safeZone', on: here !== null, name: here ?? this.was ?? null });
    this.was = here;
    if (here === null || p.dead) return;
    // Monsters chasing the player give it up.
    for (const m of w.entities.values()) {
      const b = m.brain;
      if (!b || b.state !== 'chase' || b.foeId !== null) continue;
      if (Math.abs(m.x - p.x) > REACH || Math.abs(m.y - p.y) > REACH) continue;
      b.state = 'return';
      m.cast = null;
      if (m.targetId === p.id) m.targetId = null;
    }
  }
}
